import SimpleReactValidator from "simple-react-validator";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Loader from "@element/Loader/Loader";
import { getUser, apiRequest } from "@utils/service";
import { toast } from "react-toastify";
import Default from "@layout/Default/Default";
import "./profile.css";

const Profile = () => {
  const user = getUser();
  const [, forceUpdate] = useState("");
  const [newImage, setNewImage] = useState<File | undefined>();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user.first_name,
    last_name: user.last_name,
    mobile: user.mobile,
    email: user.email,
    ca_classes: "",
    ca_attempts_id: "",
    city_id: "",
  });
  const profilePreviewRef = useRef("");
  const [existingProfilePic, setExistingProfilePic] = useState("");

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
    })
  );

  const getProfile = useCallback(() => {
    apiRequest("profile").then(({ data: [{ profile_pic, ca_classes }] }) => {
      setFormData((prevState) => ({
        ...prevState,
        ca_classes,
      }));
      setExistingProfilePic(profile_pic);
    });
  }, []);

  useEffect(() => {
    getProfile();
  }, [getProfile]);

  const updateProfilePic = useCallback(async () => {
    if (newImage) {
      return apiRequest("update_profile_pic", { profile_img: newImage }).then(
        (resp) => {
          if (resp.success === "true") {
            toast.success("Profile picture updated!");
            setExistingProfilePic(resp.profile_pic);
          }
        }
      );
    }
  }, [newImage]);

  const removeProfilePic = useCallback(async () => {
    return apiRequest("update_profile_pic", { profile_img: "" }).then(
      (resp) => {
        if (resp.success === "true") {
          toast.success("Profile picture removed!");
        }
        setExistingProfilePic("");
        if (profilePreviewRef.current) {
          URL.revokeObjectURL(profilePreviewRef.current);
          profilePreviewRef.current = "";
        }
      }
    );
  }, []);

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();
    const formValid = validator.current.allValid();
    if (formValid) {
      let response: any = await apiRequest("account_details_update", formData);
      await updateProfilePic();
      if (response.success === "true") {
        toast.success("Profile data updated!");
        setLoading(false);
      } else {
        setLoading(false);
        toast.error(response.message);
      }
    } else {
      validator.current.showMessages();
    }
  };

  useEffect(() => {
    console.log(newImage);
    if (newImage) {
      if (profilePreviewRef.current) {
        URL.revokeObjectURL(profilePreviewRef.current);
      }
      profilePreviewRef.current = URL.createObjectURL(newImage);
      forceUpdate(new Date().getTime().toString());
    }
  }, [newImage]);

  return (
    <Default>
      <div className="login-container">
        {loading && <Loader />}
        <div className="d-flex justify-content-center">
          <div className="card">
            <div className="card-header">
              <h3>Profile</h3>
            </div>
            <div className="card-body">
              <form onSubmit={(e) => handleFormSubmit(e)}>
                <div>
                  <div>
                    <label
                      htmlFor="profile-pic-input"
                      className="h-4 bordered border-white"
                      style={{
                        backgroundImage: `url(${
                          existingProfilePic ||
                          profilePreviewRef.current ||
                          "/logo192.png"
                        })`,
                      }}
                    >
                      {existingProfilePic && (
                        <button onClick={removeProfilePic} type="button">
                          <i className="fa fa-times"></i>
                        </button>
                      )}
                    </label>
                    <input
                      className="d-none"
                      id="profile-pic-input"
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        if (event.target?.files?.length) {
                          setNewImage(event.target.files[0]);
                        }
                      }}
                      name="profile_pic"
                    />
                  </div>
                </div>
                <div className="input-group form-group mb-3 d-flex justify-content-center">
                  <div className="d-flex">
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        <i className="fas fa-user"></i>
                      </span>
                    </div>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="First Name"
                    />
                  </div>

                  {validator.current.message(
                    "first_name",
                    formData.first_name,
                    "required|min:2|max:50",
                    { className: "text-danger" }
                  )}
                </div>
                <div className="input-group form-group mb-3 d-flex justify-content-center">
                  <div className="d-flex">
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        <i className="fas fa-user"></i>
                      </span>
                    </div>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Last Name"
                    />
                  </div>

                  {validator.current.message(
                    "last_name",
                    formData.last_name,
                    "required|min:2|max:50",
                    { className: "text-danger" }
                  )}
                </div>
                <div className="input-group form-group mb-3 d-flex justify-content-center">
                  <div className="d-flex">
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        <i className="fas fa-mobile"></i>
                      </span>
                    </div>
                    <input
                      type="text"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Mobile Number"
                    />
                  </div>

                  {validator.current.message(
                    "mobile",
                    formData.mobile,
                    "required|phone:10",
                    { className: "text-danger" }
                  )}
                </div>
                <div className="input-group form-group mb-3 d-flex justify-content-center">
                  <div className="d-flex">
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        <i className="fas fa-envelope"></i>
                      </span>
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Email Address"
                    />
                  </div>

                  {validator.current.message(
                    "email",
                    formData.email,
                    "required|email",
                    { className: "text-danger" }
                  )}
                </div>
                <div className="input-group form-group mb-3 d-flex justify-content-center">
                  <div className="d-flex">
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        <i className="fas fa-clipboard"></i>
                      </span>
                    </div>
                    <input
                      type="text"
                      name="ca_classes"
                      value={formData.ca_classes}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Classes (if any)"
                    />
                  </div>

                  {validator.current.message(
                    "ca_classes",
                    formData.ca_classes,
                    "required|string",
                    { className: "text-danger" }
                  )}
                </div>
                <div className="form-group">
                  <input
                    disabled={loading}
                    type="submit"
                    value="Update"
                    className="btn float-right login_btn"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Default>
  );
};

export default Profile;
